import { Injectable, inject } from '@angular/core';
import {Observable} from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {environment} from '../../environments/environment';
import {TenantService} from './tenant.service';
import {PricingPlanDetails} from '../models/pricing-plan-details.model';
import { PaymentMethodConfigurationResponse } from '../models/payment-method-configuration.model';


@Injectable({
  providedIn: 'root'
})
export class StripeConnectionService {
  private http = inject(HttpClient);
  private tenant = inject(TenantService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() {


  }

  initStripeConnection(authorizationCode: string): Observable<any> {

    const headers = new HttpHeaders().set('Content-Type', "application/json");

    return this.http.post(environment.api.stripeConnectionUrl, {authorizationCode, tenantId: this.tenant.id}, {headers});
  }

  /**
   * Creates the school's plans on first run.
   *
   * QUARTERLY IS OPTIONAL AND OPT-IN. `/api/init-pricing-plans` has accepted
   * `quarterlyPlanPrice` / `quarterlyPlanDescription` since 2026-08-26, but nothing on the
   * client ever sent them, so the backend support was unreachable. The two fields are
   * appended here rather than inserted, and only when a price was actually entered, so:
   *   - every existing caller keeps working unchanged, and
   *   - a school that leaves quarterly blank gets exactly the three plans it always did.
   */
  setupDefaultPricingPlans(monthlyPlanDescription:string, yearlyPlanDescription:string,
                           monthlyPlanPrice:number, yearlyPlanPrice: number,
                           lifetimeAccessPrice:number,
                           teamPlanThreshold:number,
                           teamPlanDiscount:number,
                           quarterlyPlanPrice?: number,
                           quarterlyPlanDescription?: string): Observable<PricingPlanDetails> {


    return this.http.post<PricingPlanDetails>(environment.api.stripeInitPricingPlansUrl,
      {
        monthlyPlanDescription,
        yearlyPlanDescription,
        monthlyPlanPrice,
        yearlyPlanPrice,
        lifetimeAccessPrice,
        teamPlanThreshold,
        teamPlanDiscount,
        // Omitted entirely when there is no price, so the controller's own opt-in check
        // (`req.body.quarterlyPlanPrice ? ... : null`) sees nothing and creates no plan.
        ...(quarterlyPlanPrice ? {
          quarterlyPlanPrice,
          quarterlyPlanDescription: quarterlyPlanDescription || 'Quarterly Plan'
        } : {}),
        tenantId: this.tenant?.id,
      });
  }

  /**
   * Method to call the backend endpoint for checking and enabling Stripe Tax.
   * @param testUser - Flag indicating if the operation is for a test user.
   * @param taxBehavior - The tax behavior to be applied (either 'inclusive' or 'exclusive').
   * @returns Observable containing the response from the backend.
   */
  checkAndEnableStripeTax(testUser: boolean, taxBehavior: string): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const endpointUrl = environment.api.checkEnableStripeTaxUrl;
    return this.http.post(
      endpointUrl,
      { tenantId: this.tenant.id, testUser, taxBehavior },
      { headers }
    );
  }

  /**
   * Fetches payment method configurations from Stripe
   * @param testUser - Flag indicating if the operation is for a test user
   * @returns Observable containing the payment method configurations
   */
  getPaymentMethodConfigurations(testUser: boolean): Observable<PaymentMethodConfigurationResponse> {
    const params = {
      tenantId: this.tenant.id,
      testUser: testUser.toString()
    };

    return this.http.get<PaymentMethodConfigurationResponse>(
      environment.api.getPaymentMethodsConfigUrl,
      { params }
    );
  }

  /**
   * Switch a single payment method on or off for this school's checkout.
   *
   * The tenant is taken from the authenticated session on the server, not sent from here.
   */
  updatePaymentMethodPreference(method: string, enabled: boolean, testUser: boolean) {
    return this.http.post<{
      success: boolean;
      method?: string;
      enabled?: boolean;
      message?: string;
    }>(environment.api.updatePaymentMethodPreferenceUrl, { method, enabled, testUser });
  }
}
